/**
 * Eigenständige Ansteuerung des SUNMI-T3-Kundendisplays (Entscheidung E-S2, 04.11.2026): Die
 * Kasse schickt dieselben Nachrichten wie an die Kundenanzeige im zweiten Browserfenster
 * (`KundenanzeigeNachricht`); daraus entsteht hier der Anzeigestand und über
 * `kundendisplayZeilen` der Text für `BilanxGeraet.kundendisplay` (native `ZeilenPresentation`).
 *
 * - Hängt nur an der Android-Bridge (`findeGeraet`), nicht am nativen Bondruck (S-1 /
 *   `BILANX_NATIVDRUCK`) und nicht am Modul „Kundenanzeige“.
 * - Fire-and-forget: `nachricht` arbeitet synchron und wirft nie; Gerätefehler werden höchstens
 *   ein paarmal nachgesendet und sonst verworfen. Der Verkauf wartet nie auf das Display.
 * - Keine Altdaten: „Vielen Dank“ steht `DANKE_MS`, danach Begrüßung. Leeren oder Abbrechen des
 *   Bons zeigt sofort die Begrüßung; Verlassen des Verkaufs (Retoure, Belege, …) ebenso.
 * - Inhalt nur aus dem Anzeigestand: Positionen, Summen, Zahlstand. Nie Bediener, PIN, Notizen,
 *   Fehlermeldungen oder Schlüssel.
 */
import { DANKE_MS, LEERER_STAND, naechsterStand } from '../kundenanzeige';
import type { KundenanzeigeNachricht, KundenanzeigeStand } from '../kundenanzeige';
import type { BilanxGeraet } from './bridge';
import {
  KundendisplaySender,
  kundendisplayZeilen,
  T3_DISPLAY_BREITE,
  type DisplayOptionen,
} from './kundendisplay';

/** Abstand der Nachsendeversuche, wenn das Gerät einen Stand nicht bestätigt hat. */
export const NACHSENDEN_MS = 5_000;
/** Höchstens so viele Nachsendeversuche je Stand. */
export const NACHSENDEN_MAX = 3;

export interface SteuerungUmgebung {
  setTimeout: (rueckruf: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

export interface SteuerungOptionen extends Omit<DisplayOptionen, 'training'> {
  dankeMs?: number;
  nachsendenMs?: number;
  nachsendenMax?: number;
}

const STANDARD_UMGEBUNG: SteuerungUmgebung = {
  setTimeout: (r, ms) => globalThis.setTimeout(r, ms),
  clearTimeout: (h) => {
    globalThis.clearTimeout(h as ReturnType<typeof globalThis.setTimeout>);
  },
};

export class KundendisplaySteuerung {
  private stand: KundenanzeigeStand = LEERER_STAND;
  private training = false;
  private dankeTimer: unknown = null;
  private nachsendeTimer: unknown = null;
  private versuche = 0;
  private aktiv = false;
  private readonly sender: KundendisplaySender;
  private readonly anzeige: DisplayOptionen;

  constructor(
    geraet: BilanxGeraet,
    private readonly opt: SteuerungOptionen = {},
    private readonly umgebung: SteuerungUmgebung = STANDARD_UMGEBUNG,
  ) {
    this.anzeige = {
      breite: opt.breite ?? T3_DISPLAY_BREITE,
      ...(opt.zeilen === undefined ? {} : { zeilen: opt.zeilen }),
      ...(opt.begruessung === undefined ? {} : { begruessung: opt.begruessung }),
    };
    this.sender = new KundendisplaySender(geraet, this.anzeige);
  }

  /** Aktueller Anzeigestand (für Tests und Diagnose). */
  get aktuellerStand(): KundenanzeigeStand {
    return this.stand;
  }

  /** Zeilen, die das Display gerade zeigen soll. */
  zeilen(): string[] {
    return kundendisplayZeilen(this.stand, { ...this.anzeige, training: this.training });
  }

  /**
   * Beim Laden der Kasse: aktuellen Stand zeigen – anfangs die Begrüßung (beendet die Spiegelung
   * der Kassenoberfläche). Setzt den Stand bewusst nicht zurück: Die Effekte des Verkaufs laufen
   * vor denen der App und haben einen wiederhergestellten Bon womöglich schon übergeben (auch
   * beim StrictMode-Neustart; Codex MITTEL 1).
   */
  start(): void {
    this.aktiv = true;
    this.sender.fortsetzen();
    if (this.stand.modus === 'bezahlt' && this.dankeTimer === null) {
      // Danke-Timer ging beim stopp() verloren: nicht stehen lassen
      this.stand = LEERER_STAND;
    }
    this.zeige();
  }

  /** Abbau der Kasse: Timer weg, Wartendes verworfen, nichts mehr senden. */
  stopp(): void {
    this.aktiv = false;
    this.stoppeTimer();
    this.sender.stoppe();
  }

  /** Verkauf verlassen (Retoure, Belege, Abschluss …): sofort Begrüßung, Training aus. */
  beenden(): void {
    this.zuruecksetzen();
    this.training = false;
    this.zeige();
  }

  setzeTraining(training: boolean): void {
    if (this.training === training) {
      return;
    }
    this.training = training;
    this.zeige();
  }

  /** Nachricht der Kasse wie an die Kundenanzeige; wirft nie. */
  nachricht(n: KundenanzeigeNachricht): void {
    try {
      this.verarbeite(n);
    } catch {
      // Das Display ist Beiwerk – nie den Verkauf aufhalten.
    }
  }

  private verarbeite(n: KundenanzeigeNachricht): void {
    switch (n.typ) {
      case 'bon':
        if (n.positionen.length === 0) {
          // Nach dem Kassieren leert die Kasse den Bon: „Vielen Dank“ bleibt bis zum Ablauf.
          // Sonst (Bon geleert/abgebrochen): sofort Begrüßung.
          if (this.stand.modus === 'bezahlt') {
            return;
          }
          this.zuruecksetzen();
        } else {
          this.stoppeDanke();
          this.stand = naechsterStand(
            this.stand.modus === 'bezahlt' ? LEERER_STAND : this.stand,
            n,
          );
        }
        break;
      case 'zahlung':
        this.stand = naechsterStand(this.stand, n);
        break;
      case 'bezahlt':
        this.stoppeDanke();
        this.stand = naechsterStand(this.stand, n);
        this.dankeTimer = this.umgebung.setTimeout(() => {
          this.dankeTimer = null;
          if (this.stand.modus === 'bezahlt') {
            this.stand = LEERER_STAND;
            this.zeige();
          }
        }, this.opt.dankeMs ?? DANKE_MS);
        break;
      case 'leer':
      case 'ende':
        this.zuruecksetzen();
        break;
      case 'puls':
      case 'anfrage':
        return;
    }
    this.zeige();
  }

  private zuruecksetzen(): void {
    this.stoppeDanke();
    this.stand = LEERER_STAND;
  }

  private stoppeDanke(): void {
    if (this.dankeTimer !== null) {
      this.umgebung.clearTimeout(this.dankeTimer);
      this.dankeTimer = null;
    }
  }

  private stoppeTimer(): void {
    this.stoppeDanke();
    if (this.nachsendeTimer !== null) {
      this.umgebung.clearTimeout(this.nachsendeTimer);
      this.nachsendeTimer = null;
    }
  }

  private zeige(nachsenden = false): void {
    if (!this.aktiv) {
      return;
    }
    if (!nachsenden) {
      this.versuche = 0;
      if (this.nachsendeTimer !== null) {
        this.umgebung.clearTimeout(this.nachsendeTimer);
        this.nachsendeTimer = null;
      }
    }
    const zeilen = this.zeilen();
    this.sender
      .sende(zeilen)
      .then(() => {
        this.pruefeBestaetigung();
      })
      .catch(() => {
        this.pruefeBestaetigung();
      });
  }

  /** Nicht bestätigt und noch aktuell: später erneut senden (begrenzt). */
  private pruefeBestaetigung(): void {
    // Lief schon ein Auftrag, prüft dessen Aufrufer am Ende den neuesten Stand.
    if (!this.aktiv || this.sender.beschaeftigt) {
      return;
    }
    const soll = this.zeilen();
    if (this.sender.angezeigt(soll)) {
      return;
    }
    if (
      this.nachsendeTimer !== null ||
      this.versuche >= (this.opt.nachsendenMax ?? NACHSENDEN_MAX)
    ) {
      return;
    }
    this.versuche += 1;
    this.nachsendeTimer = this.umgebung.setTimeout(() => {
      this.nachsendeTimer = null;
      this.zeige(true);
    }, this.opt.nachsendenMs ?? NACHSENDEN_MS);
  }
}
